import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Search, Crown, Check, ShieldCheck, ShieldX, Clock, RotateCcw, X } from 'lucide-react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const APPROVAL_FILTERS = [
  { value: 'all', label: 'All Users' },
  { value: 'pending', label: 'Pending Approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const APPROVAL_BADGE = {
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-800 border-amber-300', icon: Clock },
  approved: { label: 'Approved', className: 'bg-green-100 text-green-800 border-green-300', icon: ShieldCheck },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800 border-red-300', icon: ShieldX },
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [approvalFilter, setApprovalFilter] = useState('all');
  const [plans, setPlans] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditingPlan, setIsEditingPlan] = useState(false);
  const [newPlan, setNewPlan] = useState('');
  const [updatingPlan, setUpdatingPlan] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectUserId, setRejectUserId] = useState(null);

  const getToken = () => localStorage.getItem('admin_token');

  useEffect(() => {
    fetchUsers();
    fetchPlans();
  }, []);

  const applyFilters = useCallback(() => {
    let result = [...users];
    if (approvalFilter !== 'all') {
      result = result.filter(u => (u.approval_status || 'approved') === approvalFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(u =>
        u.name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.company_name?.toLowerCase().includes(term)
      );
    }
    setFilteredUsers(result);
  }, [users, searchTerm, approvalFilter]);

  useEffect(() => { applyFilters(); }, [applyFilters]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscription-plans`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const handleApprove = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API}/users/${userId}/approve`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, approval_status: 'approved' } : u));
      toast.success('User approved — notification email sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectDialog = (userId) => {
    setRejectUserId(userId);
    setRejectReason('');
    setShowRejectDialog(true);
  };

  const handleReject = async () => {
    if (!rejectUserId) return;
    setActionLoading(rejectUserId);
    setShowRejectDialog(false);
    try {
      await axios.post(`${API}/users/${rejectUserId}/reject`,
        rejectReason ? { reason: rejectReason } : {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setUsers(prev => prev.map(u => u.id === rejectUserId ? { ...u, approval_status: 'rejected' } : u));
      toast.success('User rejected — notification email sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject user');
    } finally {
      setActionLoading(null);
      setRejectUserId(null);
    }
  };

  const handleResetApproval = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API}/users/${userId}/reset-approval`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, approval_status: 'pending' } : u));
      toast.success('User reset to pending');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset approval');
    } finally {
      setActionLoading(null);
    }
  };

  const getPlanDisplayName = (planName) => {
    const plan = plans.find(p => p.name === planName);
    return plan?.display_name || planName || 'Pay as you go';
  };

  const getPlanColor = (planName) => {
    const colors = {
      pay_as_you_go: 'bg-slate-100 text-slate-700 border-slate-300',
      starter_pack: 'bg-blue-100 text-blue-700 border-blue-300',
      creator_pack: 'bg-purple-100 text-purple-700 border-purple-300',
      studio_pack: 'bg-amber-100 text-amber-700 border-amber-300',
      free: 'bg-slate-100 text-slate-700 border-slate-300',
      starter: 'bg-blue-100 text-blue-700 border-blue-300',
      pro: 'bg-purple-100 text-purple-700 border-purple-300',
      enterprise: 'bg-amber-100 text-amber-700 border-amber-300',
    };
    return colors[planName] || colors.pay_as_you_go;
  };

  const getPlanIcon = (planName) => {
    if (['studio_pack', 'creator_pack', 'enterprise', 'pro'].includes(planName)) {
      return <Crown className="w-3 h-3 mr-1" />;
    }
    return null;
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return 'Free';
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const handleEditPlan = (user) => {
    setSelectedUser(user);
    setNewPlan(user.subscription_plan || 'pay_as_you_go');
    setIsEditingPlan(true);
  };

  const handleUpdatePlan = async () => {
    if (!selectedUser || !newPlan) return;
    setUpdatingPlan(true);
    try {
      const response = await axios.put(
        `${API}/users/${selectedUser.id}/subscription`,
        { plan_name: newPlan },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setUsers(prev => prev.map(u => u.id === selectedUser.id ? { ...u, subscription_plan: newPlan } : u));
      const syncStatus = response.data.synced_to_user_backend
        ? ' (synced to user backend)'
        : ' (local only - user backend sync pending)';
      toast.success(`Subscription updated to ${getPlanDisplayName(newPlan)}${syncStatus}`);
      setIsEditingPlan(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error('Failed to update subscription');
    } finally {
      setUpdatingPlan(false);
    }
  };

  const pendingCount = users.filter(u => u.approval_status === 'pending').length;

  const renderApprovalActions = (user) => {
    const status = user.approval_status || 'approved';
    const isLoading = actionLoading === user.id;

    if (status === 'pending') {
      return (
        <div className="flex gap-1.5">
          <Button size="sm" disabled={isLoading} onClick={() => handleApprove(user.id)}
            data-testid={`approve-btn-${user.id}`}
            className="bg-green-600 hover:bg-green-700 text-white text-xs px-2.5 py-1 h-7">
            <ShieldCheck className="w-3.5 h-3.5 mr-1" />{isLoading ? '...' : 'Approve'}
          </Button>
          <Button size="sm" variant="destructive" disabled={isLoading} onClick={() => openRejectDialog(user.id)}
            data-testid={`reject-btn-${user.id}`}
            className="text-xs px-2.5 py-1 h-7">
            <ShieldX className="w-3.5 h-3.5 mr-1" />{isLoading ? '...' : 'Reject'}
          </Button>
        </div>
      );
    }
    if (status === 'rejected') {
      return (
        <Button size="sm" variant="outline" disabled={isLoading} onClick={() => handleResetApproval(user.id)}
          data-testid={`reset-btn-${user.id}`}
          className="text-xs px-2.5 py-1 h-7 text-amber-700 border-amber-300 hover:bg-amber-50">
          <RotateCcw className="w-3.5 h-3.5 mr-1" />{isLoading ? '...' : 'Reset to Pending'}
        </Button>
      );
    }
    return null;
  };

  if (loading) {
    return <div className="text-center py-8">Loading users...</div>;
  }

  return (
    <div data-testid="users-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-800 mb-2">Users Management</h1>
        <p className="text-slate-600">View and manage all users, subscriptions, and approval status</p>
      </div>

      {/* Pending banner */}
      {pendingCount > 0 && (
        <div data-testid="pending-banner" className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-amber-600" />
            <span className="text-amber-800 font-medium">
              {pendingCount} user{pendingCount > 1 ? 's' : ''} awaiting approval
            </span>
          </div>
          <Button size="sm" variant="outline" onClick={() => setApprovalFilter('pending')}
            className="text-amber-700 border-amber-300 hover:bg-amber-100 text-xs">
            View Pending
          </Button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200">
        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              data-testid="user-search-input"
              type="text"
              placeholder="Search by name, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-1.5" data-testid="approval-filter">
            {APPROVAL_FILTERS.map(f => (
              <Button key={f.value} size="sm" variant={approvalFilter === f.value ? 'default' : 'outline'}
                onClick={() => setApprovalFilter(f.value)}
                className={`text-xs ${approvalFilter === f.value ? 'bg-purple-600 hover:bg-purple-700' : ''}`}>
                {f.label}
                {f.value === 'pending' && pendingCount > 0 && (
                  <span className="ml-1.5 bg-amber-500 text-white rounded-full px-1.5 text-[10px] font-bold">{pendingCount}</span>
                )}
              </Button>
            ))}
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-500">{approvalFilter !== 'all' ? `No ${approvalFilter} users found` : 'No users found'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Name</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Phone</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Company</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Approval</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Subscription</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Credits</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Joined</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const status = user.approval_status || 'approved';
                  const badge = APPROVAL_BADGE[status] || APPROVAL_BADGE.approved;
                  const Icon = badge.icon;
                  return (
                    <tr key={user.id} className={`border-b border-slate-100 hover:bg-slate-50 ${status === 'pending' ? 'bg-amber-50/30' : ''}`} data-testid={`user-row-${user.id}`}>
                      <td className="py-3 px-4 text-sm text-slate-700 font-medium">{user.name}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{user.email}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{user.phone || '-'}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{user.company_name || '-'}</td>
                      <td className="py-3 px-4">
                        <span data-testid={`approval-badge-${user.id}`}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${badge.className}`}>
                          <Icon className="w-3 h-3" />{badge.label}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getPlanColor(user.subscription_plan)}`}
                          data-testid={`user-plan-${user.id}`}>
                          {getPlanIcon(user.subscription_plan)}
                          {getPlanDisplayName(user.subscription_plan)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm font-semibold text-slate-700">
                        {(user.credits || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {user.joined_date || user.created_at ? format(new Date(user.joined_date || user.created_at), 'MMM dd, yyyy') : 'N/A'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {renderApprovalActions(user)}
                          <Button size="sm" variant="outline" onClick={() => handleEditPlan(user)}
                            data-testid={`edit-plan-btn-${user.id}`} className="text-xs h-7">
                            Change Plan
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject User</DialogTitle>
            <DialogDescription>
              The user will be notified via email. Optionally provide a reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              data-testid="reject-reason-input"
              placeholder="Optional: Reason for rejection (will be included in the email)..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button variant="destructive" data-testid="confirm-reject-btn" onClick={handleReject}>
              <ShieldX className="w-4 h-4 mr-1" /> Reject User
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Plan Dialog */}
      <Dialog open={isEditingPlan} onOpenChange={setIsEditingPlan}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Subscription Plan</DialogTitle>
            <DialogDescription>
              Update the subscription plan for {selectedUser?.name || selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Current Plan</label>
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getPlanColor(selectedUser?.subscription_plan)}`}>
                {getPlanIcon(selectedUser?.subscription_plan)}
                {getPlanDisplayName(selectedUser?.subscription_plan)}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">New Plan</label>
              <Select value={newPlan} onValueChange={setNewPlan}>
                <SelectTrigger data-testid="plan-select"><SelectValue placeholder="Select a plan" /></SelectTrigger>
                <SelectContent>
                  {plans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.name} data-testid={`plan-option-${plan.name}`}>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPlanColor(plan.name)}`}>
                          {getPlanIcon(plan.name)}{plan.display_name}
                        </span>
                        <span className="text-slate-500 text-xs">
                          ({plan.monthly_credits?.toLocaleString('en-IN') || 0} credits) - {formatPrice(plan.price)}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {newPlan && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-slate-800">{getPlanDisplayName(newPlan)}</h4>
                  <span className="text-sm font-semibold text-purple-600">
                    {formatPrice(plans.find(p => p.name === newPlan)?.price)}
                    {plans.find(p => p.name === newPlan)?.billing_cycle === 'monthly' && '/mo'}
                  </span>
                </div>
                <div className="text-sm text-slate-600 mb-2">
                  {(plans.find(p => p.name === newPlan)?.monthly_credits || 0).toLocaleString('en-IN')} credits
                  {plans.find(p => p.name === newPlan)?.billing_cycle === 'monthly' ? '/month' : ''}
                </div>
                {plans.find(p => p.name === newPlan)?.features?.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />{feature}
                  </div>
                ))}
              </div>
            )}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <strong>Note:</strong> Plan changes will be saved locally. If the User Backend has subscription management enabled, changes will also be synced there.
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setIsEditingPlan(false)} disabled={updatingPlan}>Cancel</Button>
            <Button onClick={handleUpdatePlan} disabled={updatingPlan || newPlan === selectedUser?.subscription_plan}
              data-testid="confirm-plan-change-btn">
              {updatingPlan ? 'Updating...' : 'Update Plan'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
